import { useState, useEffect, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Input,
  Icon,
  useToast,
  Spinner,
  Heading,
} from '@chakra-ui/react';
import { MessageCircle, Send, Sparkles } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { db, functionsUsEast1 } from '../../../../utils/init-firebase';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  Timestamp,
  getDocs,
  limit,
} from 'firebase/firestore';
import { COLLECTION_NAMES } from '../../types';

interface RockChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  rockId: string;
  rockTitle: string;
  evaluationResult?: any;
  currentUserId?: string;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  createdAt: Timestamp;
}

/**
 * Rock Chat Modal Component
 * Modal for AI chat conversations about a rock
 * Migrated from Tailwind to Chakra UI
 */
export function RockChatModal({
  isOpen,
  onClose,
  rockId,
  rockTitle,
  evaluationResult,
  currentUserId,
}: RockChatModalProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [conversationError, setConversationError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const toast = useToast();

  // Find or create conversation
  useEffect(() => {
    if (!isOpen || !currentUserId) {
      setConversationId(null);
      setIsInitializing(false);
      setConversationError(null);
      return;
    }

    const findOrCreateConversation = async () => {
      setIsInitializing(true);
      setConversationError(null);
      
      try {
        // Try to find existing conversation
        // First try with orderBy (requires index), fallback to simple query if index missing
        let snapshot;
        try {
          const conversationsQuery = query(
            collection(db, COLLECTION_NAMES.AI_CONVERSATIONS),
            where('rockId', '==', rockId),
            where('userId', '==', currentUserId),
            orderBy('createdAt', 'desc'),
            limit(1)
          );
          snapshot = await getDocs(conversationsQuery);
        } catch (queryError: any) {
          // If orderBy query fails due to missing index, try without orderBy
          // This will get any matching conversation (may not be the most recent, but will work)
          console.warn('Query with orderBy failed (likely missing index), trying without:', queryError);
          const fallbackQuery = query(
            collection(db, COLLECTION_NAMES.AI_CONVERSATIONS),
            where('rockId', '==', rockId),
            where('userId', '==', currentUserId),
            limit(1)
          );
          snapshot = await getDocs(fallbackQuery);
        }

        if (!snapshot.empty) {
          const conv = snapshot.docs[0];
          setConversationId(conv.id);
        } else {
          // Create new conversation
          const newConv = await addDoc(collection(db, COLLECTION_NAMES.AI_CONVERSATIONS), {
            rockId,
            userId: currentUserId,
            title: `Chat about: ${rockTitle}`,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
          });
          setConversationId(newConv.id);
        }
        setConversationError(null);
      } catch (error: any) {
        console.error('Error finding/creating conversation:', error);
        const errorMessage = error?.message || 'Failed to initialize chat conversation';
        setConversationError(errorMessage);
        toast({
          title: 'Error',
          description: errorMessage,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsInitializing(false);
      }
    };

    findOrCreateConversation();
  }, [isOpen, rockId, currentUserId, rockTitle, toast]);

  // Load messages
  useEffect(() => {
    if (!conversationId) return;

    const messagesQuery = query(
      collection(db, COLLECTION_NAMES.AI_CONVERSATION_MESSAGES),
      where('conversationId', '==', conversationId),
      orderBy('createdAt', 'asc')
    );

    const unsubscribe = onSnapshot(
      messagesQuery,
      (snapshot) => {
        const messagesData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt || Timestamp.now(),
        })) as ChatMessage[];
        setMessages(messagesData);
      },
      (error) => {
        console.error('Error loading messages:', error);
      }
    );

    return () => unsubscribe();
  }, [conversationId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (messageText: string) => {
    if (!messageText.trim() || !conversationId || !currentUserId || isLoading) return;

    setIsLoading(true);

    try {
      // Use functionsUsEast1 since chatAboutRock is deployed in us-east1
      const chatAboutRockFunction = httpsCallable(functionsUsEast1, 'chatAboutRock');

      await chatAboutRockFunction({
        conversationId,
        rockId,
        userMessage: messageText.trim(),
        evaluationContext: evaluationResult || null,
      });
    } catch (error: any) {
      console.error('Error sending message:', error);
      toast({
        title: 'Error',
        description: error?.message || 'Failed to send message. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    const userMessageText = inputMessage.trim();
    setInputMessage('');
    await sendMessage(userMessageText);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={2}>
            <Icon as={MessageCircle} w={6} h={6} color="blue.600" />
            <Text>Chat about: {rockTitle}</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch" h="400px" overflowY="auto">
            {isInitializing ? (
              <Box textAlign="center" py={8}>
                <Spinner size="md" />
                <Text color="gray.500" mt={2}>Initializing chat...</Text>
              </Box>
            ) : conversationError ? (
              <Box textAlign="center" py={8}>
                <Text color="red.500" mb={2}>Error: {conversationError}</Text>
                <Text color="gray.500" fontSize="sm">Please try closing and reopening the chat.</Text>
              </Box>
            ) : messages.length === 0 ? (
              <VStack spacing={6} align="center" py={8}>
                <Icon as={Sparkles} w={12} h={12} color="gray.400" />
                <VStack spacing={2}>
                  <Text fontWeight="semibold" color="gray.700" fontSize="md">
                    Ask questions about your Rock evaluation
                  </Text>
                  <Text color="gray.500" fontSize="sm">
                    Get insights and recommendations
                  </Text>
                </VStack>
                <VStack spacing={2} w="full" px={4}>
                  <Button
                    variant="outline"
                    colorScheme="blue"
                    size="sm"
                    w="full"
                    onClick={() => sendMessage("Why did my SMART score get this rating?")}
                    isDisabled={isLoading || !conversationId || isInitializing || !!conversationError}
                  >
                    Why did my SMART score get this rating?
                  </Button>
                  <Button
                    variant="outline"
                    colorScheme="blue"
                    size="sm"
                    w="full"
                    onClick={() => sendMessage("How can I improve my milestones?")}
                    isDisabled={isLoading || !conversationId || isInitializing || !!conversationError}
                  >
                    How can I improve my milestones?
                  </Button>
                  <Button
                    variant="outline"
                    colorScheme="blue"
                    size="sm"
                    w="full"
                    onClick={() => sendMessage("What makes a good measurable?")}
                    isDisabled={isLoading || !conversationId || isInitializing || !!conversationError}
                  >
                    What makes a good measurable?
                  </Button>
                  <Button
                    variant="outline"
                    colorScheme="blue"
                    size="sm"
                    w="full"
                    onClick={() => sendMessage("Is my timeline realistic?")}
                    isDisabled={isLoading || !conversationId || isInitializing || !!conversationError}
                  >
                    Is my timeline realistic?
                  </Button>
                </VStack>
              </VStack>
            ) : (
              messages.map((message) => (
                <Box
                  key={message.id}
                  alignSelf={message.role === 'user' ? 'flex-end' : 'flex-start'}
                  maxW="80%"
                >
                  <Box
                    bg={message.role === 'user' ? 'blue.500' : 'gray.200'}
                    color={message.role === 'user' ? 'white' : 'gray.800'}
                    borderRadius="lg"
                    p={3}
                  >
                    {message.role === 'assistant' ? (
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          p: ({ node, ...props }) => (
                            <Text fontSize="sm" mb={2} color={message.role === 'user' ? 'white' : 'gray.800'} {...props} />
                          ),
                          strong: ({ node, ...props }) => (
                            <Text
                              as="strong"
                              fontWeight="bold"
                              fontSize="sm"
                              color={message.role === 'user' ? 'white' : 'gray.800'}
                              {...props}
                            />
                          ),
                          em: ({ node, ...props }) => (
                            <Text
                              as="em"
                              fontStyle="italic"
                              fontSize="sm"
                              color={message.role === 'user' ? 'white' : 'gray.800'}
                              {...props}
                            />
                          ),
                          ul: ({ node, children, ...props }) => (
                            <Box
                              as="ul"
                              pl={4}
                              mb={2}
                              fontSize="sm"
                              color={message.role === 'user' ? 'white' : 'gray.800'}
                              style={{ listStyleType: 'disc' }}
                              {...(props as any)}
                            >
                              {children}
                            </Box>
                          ),
                          ol: ({ node, children, ...props }) => (
                            <Box
                              as="ol"
                              pl={4}
                              mb={2}
                              fontSize="sm"
                              color={message.role === 'user' ? 'white' : 'gray.800'}
                              style={{ listStyleType: 'decimal' }}
                              {...(props as any)}
                            >
                              {children}
                            </Box>
                          ),
                          li: ({ node, ...props }) => (
                            <Box
                              as="li"
                              mb={1}
                              fontSize="sm"
                              color={message.role === 'user' ? 'white' : 'gray.800'}
                              {...(props as any)}
                            />
                          ),
                          h1: ({ node, ...props }) => (
                            <Heading
                              size="sm"
                              mb={2}
                              color={message.role === 'user' ? 'white' : 'gray.800'}
                              {...props}
                            />
                          ),
                          h2: ({ node, ...props }) => (
                            <Heading
                              size="xs"
                              mb={2}
                              color={message.role === 'user' ? 'white' : 'gray.800'}
                              {...props}
                            />
                          ),
                          h3: ({ node, ...props }) => (
                            <Heading
                              size="xs"
                              mb={2}
                              color={message.role === 'user' ? 'white' : 'gray.800'}
                              {...props}
                            />
                          ),
                          code: ({ node, ...props }) => (
                            <Text
                              as="code"
                              bg={message.role === 'user' ? 'blue.600' : 'gray.300'}
                              px={1}
                              py={0.5}
                              borderRadius="sm"
                              fontSize="xs"
                              fontFamily="mono"
                              color={message.role === 'user' ? 'white' : 'gray.800'}
                              {...props}
                            />
                          ),
                          pre: ({ node, ...props }) => (
                            <Box
                              as="pre"
                              bg={message.role === 'user' ? 'blue.600' : 'gray.300'}
                              p={2}
                              borderRadius="sm"
                              overflowX="auto"
                              mb={2}
                              fontSize="xs"
                              color={message.role === 'user' ? 'white' : 'gray.800'}
                              {...(props as any)}
                            />
                          ),
                        }}
                      >
                        {message.content}
                      </ReactMarkdown>
                    ) : (
                      <Text fontSize="sm">{message.content}</Text>
                    )}
                  </Box>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    {message.role === 'user' ? 'You' : 'AI'}
                  </Text>
                </Box>
              ))
            )}
            {isLoading && (
              <Box alignSelf="flex-start">
                <HStack spacing={2}>
                  <Spinner size="sm" />
                  <Text fontSize="sm" color="gray.500">AI is thinking...</Text>
                </HStack>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={2} w="full">
            <Input
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={isInitializing ? "Initializing..." : conversationError ? "Error - chat unavailable" : "Type your message..."}
              isDisabled={isLoading || !conversationId || isInitializing || !!conversationError}
            />
            <Button
              colorScheme="blue"
              leftIcon={<Icon as={Send} w={4} h={4} />}
              onClick={handleSendMessage}
              isDisabled={isLoading || !inputMessage.trim() || !conversationId || isInitializing || !!conversationError}
            >
              Send
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

